import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RefreshCw } from "lucide-react";

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export function ActivityPanel({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [team, setTeam] = useState([]);
  const [filters, setFilters] = useState({ member_id: "all", module: "all", since: "", until: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (filters.member_id !== "all") params.member_id = filters.member_id;
    if (filters.module !== "all") params.module = filters.module;
    if (filters.since) params.since = filters.since;
    if (filters.until) params.until = filters.until;
    api.get("/audit", { params })
      .then(({ data }) => setRows(data))
      .catch((err) => console.error("Could not load the activity trail", err))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/audit/modules").then(({ data }) => setModules(data)).catch((err) => console.error("Could not load audit modules", err));
    if (isAdmin) {
      api.get("/team").then(({ data }) => setTeam(data)).catch((err) => console.error("Could not load the team", err));
    }
  }, [isAdmin]);

  return (
    <Card className="studio-card" data-testid="activity-panel">
      <CardHeader className="flex-row items-center gap-3">
        <div>
          <CardTitle className="text-base">Activity trail</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {isAdmin ? "Everything everyone in this workspace has changed." : "Everything you've changed here."}
          </p>
        </div>
        <Button variant="outline" size="sm" className="ml-auto" data-testid="activity-refresh" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          {isAdmin && (
            <div className="w-44">
              <Label className="text-xs">Person</Label>
              <Select value={filters.member_id}
                      onValueChange={(v) => setFilters({ ...filters, member_id: v })}>
                <SelectTrigger data-testid="activity-filter-member"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone</SelectItem>
                  {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="w-44">
            <Label className="text-xs">Module</Label>
            <Select value={filters.module} onValueChange={(v) => setFilters({ ...filters, module: v })}>
              <SelectTrigger data-testid="activity-filter-module"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {modules.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" data-testid="activity-filter-since" value={filters.since}
                   onChange={(e) => setFilters({ ...filters, since: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" data-testid="activity-filter-until" value={filters.until}
                   onChange={(e) => setFilters({ ...filters, until: e.target.value })} />
          </div>
        </div>

        <div className="divide-y divide-border">
          {rows.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground py-2" data-testid="activity-empty">
              Nothing logged for these filters yet.
            </p>
          )}
          {rows.map((r) => (
            <div key={`${r.actor_id}-${r.at}-${r.description}`}
                 className="py-2.5 flex items-start gap-3 text-sm" data-testid="activity-row">
              <Badge variant="outline" className="shrink-0 mt-0.5">{r.module}</Badge>
              <div className="min-w-0">
                <div className="truncate">
                  <span className="font-medium">{r.actor_name}</span>{" "}
                  <span className="text-muted-foreground">{r.description}</span>
                </div>
                <div className="text-xs text-muted-foreground">{when(r.at)}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
